import { useState, useMemo, useRef, useEffect } from "react";
import FootballMatches from "./FootballMatches";
import { useOddsStore } from "../stores/store";

function ContentItem({ league, isExpanded, onToggle }) {
  const fixtures = league.fixtures;
  const sortStatus = useOddsStore((state) => state.sort.status);

  const containerRef = useRef(null);
  const itemHeight = 100; // Height of each match item
  const buffer = 2;
  const windowSize = 10;
  const [scrollIndex, setScrollIndex] = useState(0);

  const getMaxOdd = (match) =>
    match.odds_values.reduce((max, current) => {
      const currentOdd = parseFloat(current.odd);
      return currentOdd > max ? currentOdd : max;
    }, 0);

  const getMinOdd = (match) =>
    match.odds_values.reduce((min, current) => {
      const currentOdd = parseFloat(current.odd);
      return currentOdd < min ? currentOdd : min;
    }, Infinity);

  const getSortedFixtures = () => {
    const clonedFixtures = [...fixtures];
    switch (sortStatus) {
      case "OddsMax":
        return clonedFixtures.sort((a, b) => {
          const maxA = getMaxOdd(a);
          const maxB = getMaxOdd(b);
          if (maxA === maxB) {
            const secondMaxA =
              a.odds_values
                .map((o) => parseFloat(o.odd))
                .sort((x, y) => y - x)[1] || 0;
            const secondMaxB =
              b.odds_values
                .map((o) => parseFloat(o.odd))
                .sort((x, y) => y - x)[1] || 0;
            return secondMaxB - secondMaxA;
          }
          return maxB - maxA;
        });
      case "OddsMin":
        return clonedFixtures.sort((a, b) => {
          const minA = getMinOdd(a);
          const minB = getMinOdd(b);
          if (minA === minB) {
            const secondMinA =
              a.odds_values
                .map((o) => parseFloat(o.odd))
                .sort((x, y) => x - y)[1] || Infinity;
            const secondMinB =
              b.odds_values
                .map((o) => parseFloat(o.odd))
                .sort((x, y) => x - y)[1] || Infinity;
            return secondMinA - secondMinB;
          }
          return minA - minB;
        });
      default:
        return clonedFixtures;
    }
  };

  const sortedFixtures = useMemo(
    () => getSortedFixtures(),
    [fixtures, sortStatus]
  );
  const totalCount = sortedFixtures.length;

  const listHeight = totalCount < 4 ? totalCount * itemHeight : 400;

  const startIndex = Math.max(0, scrollIndex - buffer);
  const endIndex = Math.min(totalCount, scrollIndex + windowSize + buffer);
  const visibleFixtures = sortedFixtures.slice(startIndex, endIndex);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const newIndex = Math.floor(scrollTop / itemHeight);

    if (newIndex !== scrollIndex) {
      setScrollIndex(newIndex);
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    if (!isExpanded) {
      setScrollIndex(0);
    } else {
      if (container) {
        container.scrollTop = 0;
        setScrollIndex(0);
      }
    }
  }, [isExpanded]);

  return (
    <div className="main-section__content-item">
      <div
        className="content-item__country-row"
        onClick={onToggle}
        style={{ cursor: "pointer" }}
      >
        <img src={league.league_logo} alt={league.league_name} />
        <div className="country-item__text">
          <span className="country">{league.country_name}</span> —{" "}
          <span className="championship">{league.league_name}</span>
        </div>
      </div>

      {isExpanded && (
        <div
          ref={containerRef}
          className="content-item__all-football-matches scroll-container"
          onScroll={handleScroll}
          style={{
            height: `${listHeight}px`,
            overflowY: totalCount >= 4 ? "auto" : "hidden",
            scrollbarWidth: "none",
            transition: "max-height 0.3s ease",
            position: "relative",
          }}
        >
          <div
            style={{
              height: `${totalCount * itemHeight}px`,
              position: "relative",
            }}
          >
            {visibleFixtures.map((match, index) => {
              const globalIndex = startIndex + index;
              const top = globalIndex * itemHeight;

              return (
                <div
                  key={globalIndex}
                  style={{
                    position: "absolute",
                    top: `${top}px`,
                    width: "100%",
                  }}
                >
                  <FootballMatches match={match} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentItem;
